import { createClient, type User } from '@supabase/supabase-js';

const url = import.meta.env.VITE_SUPABASE_URL;
const key = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;
export const cloud = url && key ? createClient(url, key) : null;

export type CloudDay = {
  day: string;
  draft: string;
  words_written: number;
  daily_goal: number;
  updated_at: string;
};
export type BoardEntry = { handle: string; word_count: number };

export async function getCloudDays(user: User): Promise<CloudDay[]> {
  if (!cloud) return [];
  const rows: CloudDay[] = [];
  for (let offset = 0; ; offset += 1000) {
    const { data, error } = await cloud.from('writing_days')
      .select('day,draft,words_written,daily_goal,updated_at')
      .eq('user_id', user.id).order('day').range(offset, offset + 999);
    if (error) throw error;
    rows.push(...(data as CloudDay[]));
    if (!data || data.length < 1000) return rows;
  }
}

export type CloudDaySummary = Omit<CloudDay, 'draft'>;
export async function getCloudDaySummaries(user: User): Promise<CloudDaySummary[]> {
  if (!cloud) return [];
  const rows: CloudDaySummary[] = [];
  for (let offset = 0; ; offset += 1000) {
    const { data, error } = await cloud.from('writing_days').select('day,words_written,daily_goal,updated_at')
      .eq('user_id', user.id).order('day').range(offset, offset + 999);
    if (error) throw error;
    rows.push(...(data ?? []));
    if (!data || data.length < 1000) return rows;
  }
}
export async function getCloudDay(user: User, day: string): Promise<CloudDay | null> {
  if (!cloud) return null;
  const { data, error } = await cloud.from('writing_days').select('day,draft,words_written,daily_goal,updated_at')
    .eq('user_id', user.id).eq('day', day).maybeSingle();
  if (error) throw error;
  return data;
}

export async function getBoard(scope: 'daily' | 'all_time', day: string): Promise<BoardEntry[]> {
  if (!cloud) return [];
  const { data, error } = await cloud.rpc('climbwriter_board', { board_scope: scope, board_day: day });
  if (error) throw error;
  return (data ?? []) as BoardEntry[];
}

export async function getWordsTogether(day: string): Promise<number> {
  if (!cloud) return 0;
  const { data, error } = await cloud.rpc('climbwriter_words_together', { board_day: day });
  if (error) throw error;
  return Number(data ?? 0);
}

export async function getPlusAccess(): Promise<boolean> {
  if (!cloud) return false;
  const { data, error } = await cloud.rpc('climbwriter_has_plus');
  if (error) throw error;
  return Boolean(data);
}

export async function billingRequest(action: 'status' | 'checkout' | 'portal', plan?: 'monthly' | 'yearly', requireTrial=false) {
  if (!cloud) throw new Error('Cloud connection is not configured.');
  const { data, error } = await cloud.functions.invoke('billing', { body: { action, plan, requireTrial } });
  if (error) {
    const response = error.context;
    if (response instanceof Response) {
      const message = await response.json().catch(() => null);
      if (message?.error) throw new Error(message.error);
    }
    throw new Error('Billing is temporarily unavailable. Please try again.');
  }
  return data as { enabled?: boolean; trialEligible?: boolean; isAdmin?: boolean; hasCustomer?: boolean; hadSubscription?: boolean; subscriptionStatus?: string; activeUntil?: string | null; url?: string };
}
